<template>
  <div class="base-card-info">
    <div class="info-card-title">
      {{ title }}
    </div>

    <el-form
      ref="formRef"
      class="flex"
      require-asterisk-position="right"
      label-position="top"
      :model="formValues"
      :rules="rules"
    >
      <div class="w-[432px] mr-[114px]">
        <el-form-item prop="payPassword" label="企业支付密码">
          <el-input
            v-model="formValues.payPassword"
            type="password"
            clearable
            autocomplete="new-password"
            placeholder="请输入"
            @keyup.enter="onKeyEnter"
          />
        </el-form-item>

        <template v-if="enableSms">
          <el-form-item prop="phone" label="验证手机号">
            <el-input :model-value="payPhone" disabled />
          </el-form-item>
          <el-form-item prop="verifyCode" class="relative" label="验证码">
            <el-input
              v-model="formValues.verifyCode"
              class="w-[314px] relative box-border pl36"
              clearable
              autocomplete="new-password"
              placeholder="请输入"
              @keyup.enter="onKeyEnter"
            >
              <template #prefix>
                <svg-icon class="left-[10px] !w-[18px] z-12 absolute" icon="lucide:shield-check" />
              </template>
            </el-input>

            <sms-button
              ref="smsBtnRef"
              class="relative !left-auto ml-[16px]"
              @click="doSendSms"
            />
          </el-form-item>
        </template>
      </div>
    </el-form>
  </div>
</template>

<script setup lang="ts">
import { SmsButton, SvgIcon } from '@vue3/components';
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { PropType, reactive, ref, toRaw } from 'vue';
import { IYibaoApplySubmitParam } from './type';
import { checkSmsCode, isNull } from '@core/tools';
import { ESmsType, sendSmscode } from '@core/api';

let sendSmsLock = false;

const emit = defineEmits<{submit: [value: IYibaoApplySubmitParam]}>();

defineOptions({ name: 'ApplySubmitForm' });

const props = defineProps({
  title: {
    type: String,
    default: '开通账户验证',
  },
  enableSms: {
    type: Boolean,
    default: false,
  },
  payPhone: {
    type: String,
    default: '',
  },
  smsType: {
    type: Number as PropType<ESmsType | undefined>,
    default: undefined,
  },
  sendSms: {
    type: Function as PropType<(phone: string) => Promise<void>>,
    default() {
      return () => {};
    },
  },
});

const formRef = ref<FormInstance>();

const formValues = ref({} as IYibaoApplySubmitParam);
const smsBtnRef = ref<InstanceType<typeof SmsButton>>(null);

const rules = reactive<FormRules<IYibaoApplySubmitParam>>({
  payPassword: [
    {
      required: true,
      message: '请输入支付密码',
    },
  ],
  verifyCode: [
    {
      required: true,
      validator: (rule: any, value: string, callback: any) => {
        if (checkSmsCode(formValues.value.verifyCode)) {
          callback(checkSmsCode(formValues.value.verifyCode));
        }
        callback();
      },
    },
  ],
});

function onKeyEnter() {
  validateForm(() => {
    emit('submit', toRaw(formValues.value));
  });
}

async function doSendSms() {
  if (sendSmsLock) return;

  try {
    sendSmsLock = true;
    smsBtnRef.value?.showLoading();

    if (!isNull(props.smsType)) {
      await sendSmscode(props.smsType, props.payPhone);
    } else {
      await props.sendSms(props.payPhone);
    }

    smsBtnRef.value?.countdown();
  } catch (error) {
    smsBtnRef.value?.hideLoading();
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  setTimeout(() => {
    sendSmsLock = false;
  }, 1300);
}

async function validateForm(
  callback?: (values: IYibaoApplySubmitParam) => void,
  failCallback?: () => void,
) {
  if (!formRef.value) return;

  // @ts-ignore
  await formRef.value.validate((valid) => {
    if (valid) {
      // if (!checkedService.value) return ElMessage.error('请阅读并同意协议');
      callback && callback(toRaw(formValues.value));
    } else {
      failCallback && failCallback();
    }
  });
}

defineExpose({
  validateForm,
  getValues: () => toRaw(formValues.value),
});
</script>

<style scoped lang="scss" src="../../../open-account/index.scss" />
